// ============================================================================
// SessionsList — the agenda at /app/sessions: every client's sessions grouped
// into TODAY / UPCOMING / PAST, each row linking to its log. A prominent
// "Log a session" CTA (and "Schedule") opens the SessionForm. When the route
// carries ?client=:id the form opens pre-scoped to that client on mount.
// Data: useCalendar (live-synced). A filter chip narrows by status.
// Reduced-motion-aware, mobile-first.
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge, Button, EmptyState } from "../../components";
import { IconCalendar, IconClock, IconPlus } from "../../components/icons";
import { useCalendar } from "../../hooks/sessions";
import { useClient } from "../../hooks/clients";
import type { CalendarSession, Client } from "../../api";
import { LoadingBlock, ErrorBlock } from "../../app/PageState";
import { SessionForm } from "./SessionForm";
import { fmtDayHeading, fmtTime } from "./lib";
import { cn } from "../../lib/cn";

const EASE = [0.22, 0.61, 0.36, 1] as const;

type Filter = "all" | "planned" | "completed";

function sessionWhen(s: CalendarSession): number {
  const iso = s.performed_at ?? s.scheduled_at;
  if (!iso) return 0;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function isSameDay(ms: number, ref: Date): boolean {
  if (ms === 0) return false;
  const d = new Date(ms);
  return (
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth() === ref.getMonth() &&
    d.getDate() === ref.getDate()
  );
}

export function SessionsList({ clientParam }: { clientParam: number | null }) {
  const { t } = useTranslation("sessions");
  const reduce = useReducedMotion();

  // A wide range so the agenda shows recent past + the booked future.
  const range = useMemo(() => {
    const start = new Date();
    start.setMonth(start.getMonth() - 3);
    const end = new Date();
    end.setMonth(end.getMonth() + 6);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
  }, []);

  const calendarQuery = useCalendar(range);
  const [filter, setFilter] = useState<Filter>("all");
  const [formOpen, setFormOpen] = useState(false);

  // Pre-scoped client (?client=) — load it and auto-open the schedule form.
  const lockedClientQuery = useClient(clientParam ?? undefined);
  const lockedClient: Client | null = lockedClientQuery.data ?? null;
  useEffect(() => {
    if (clientParam != null) setFormOpen(true);
  }, [clientParam]);

  const groups = useMemo(() => {
    const all = (calendarQuery.data ?? []).filter((s) =>
      filter === "all" ? true : s.status === filter,
    );
    const now = new Date();
    const today: CalendarSession[] = [];
    const upcoming: CalendarSession[] = [];
    const past: CalendarSession[] = [];
    for (const s of all) {
      const ms = sessionWhen(s);
      if (isSameDay(ms, now)) today.push(s);
      else if (s.status === "planned" && (ms === 0 || ms >= now.getTime()))
        upcoming.push(s);
      else past.push(s);
    }
    today.sort((a, b) => sessionWhen(a) - sessionWhen(b));
    upcoming.sort((a, b) => sessionWhen(a) - sessionWhen(b));
    past.sort((a, b) => sessionWhen(b) - sessionWhen(a));
    return { today, upcoming, past };
  }, [calendarQuery.data, filter]);

  const totalCount =
    groups.today.length + groups.upcoming.length + groups.past.length;

  const fade = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, delay: reduce ? 0 : delay, ease: EASE },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        {...fade(0)}
        className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
      >
        <div>
          <p className="text-sm font-bold uppercase tracking-kicker text-coral">
            {t("kicker")}
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tightest text-body sm:text-4xl">
            {t("title")}
          </h1>
          <p className="mt-1 text-body-soft">{t("subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setFormOpen(true)} iconLeft={<IconPlus width={18} height={18} />}>
            {t("list.logNow")}
          </Button>
        </div>
      </motion.div>

      {/* Filter chips */}
      <motion.div {...fade(0.05)} className="flex gap-2">
        {(["all", "planned", "completed"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            aria-pressed={filter === f}
            className={cn(
              "rounded-pill px-4 py-2 text-sm font-bold transition-all duration-fast",
              filter === f
                ? "bg-ink text-ondark shadow-sm"
                : "bg-surface text-body-soft ring-1 ring-inset ring-line hover:text-body",
            )}
          >
            {t(
              f === "all"
                ? "list.filterAll"
                : f === "planned"
                  ? "list.filterPlanned"
                  : "list.filterDone",
            )}
          </button>
        ))}
      </motion.div>

      {/* Body */}
      {calendarQuery.isLoading ? (
        <LoadingBlock label={t("loading")} />
      ) : calendarQuery.isError ? (
        <ErrorBlock onRetry={() => void calendarQuery.refetch()} />
      ) : totalCount === 0 ? (
        <motion.div {...fade(0.1)}>
          <EmptyState
            title={t("list.emptyTitle")}
            description={t("list.emptyBlurb")}
            action={
              <Button onClick={() => setFormOpen(true)} iconLeft={<IconPlus width={18} height={18} />}>
                {t("list.logNow")}
              </Button>
            }
          />
        </motion.div>
      ) : (
        <div className="space-y-6">
          {groups.today.length > 0 && (
            <motion.div {...fade(0.1)}>
              <Group title={t("list.today")} sessions={groups.today} />
            </motion.div>
          )}
          {groups.upcoming.length > 0 && (
            <motion.div {...fade(0.15)}>
              <Group title={t("list.upcoming")} sessions={groups.upcoming} />
            </motion.div>
          )}
          {groups.past.length > 0 && (
            <motion.div {...fade(0.2)}>
              <Group title={t("list.past")} sessions={groups.past.slice(0, 20)} />
            </motion.div>
          )}
        </div>
      )}

      {/* Schedule / log form */}
      <SessionForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        lockedClient={clientParam != null ? lockedClient : null}
      />
    </div>
  );
}

function Group({ title, sessions }: { title: string; sessions: CalendarSession[] }) {
  return (
    <section>
      <h2 className="mb-2 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
        {title}
      </h2>
      <ul className="space-y-2">
        {sessions.map((s, i) => (
          <Row key={s.id} session={s} index={i} />
        ))}
      </ul>
    </section>
  );
}

function Row({ session, index }: { session: CalendarSession; index: number }) {
  const { t } = useTranslation("sessions");
  const reduce = useReducedMotion();
  const isPlanned = session.status === "planned";
  const when = session.performed_at ?? session.scheduled_at;

  return (
    <motion.li
      initial={reduce ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: reduce ? 0 : Math.min(index * 0.03, 0.3) }}
    >
      <Link
        to={`/app/sessions/${session.id}`}
        className={cn(
          "flex items-center gap-3 rounded-md p-3 ring-1 transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
          isPlanned ? "bg-coral-soft/40 ring-coral/20" : "bg-surface ring-line",
        )}
      >
        <span
          className={cn(
            "grid h-11 w-11 shrink-0 place-items-center rounded-md text-white",
            isPlanned ? "bg-grad-coral" : "bg-grad-ocean",
          )}
        >
          {isPlanned ? (
            <IconCalendar width={18} height={18} />
          ) : (
            <IconClock width={18} height={18} />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-sm font-bold text-body">
              {session.client_name}
            </span>
            <Badge tone={isPlanned ? "volt" : "success"}>
              {t(isPlanned ? "status.planned" : "status.completed")}
            </Badge>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-body-soft">
            <span className="font-bold text-body-muted">
              {when ? `${fmtDayHeading(when)} · ${fmtTime(when)}` : t("list.noTime")}
            </span>
            {session.type && <span className="truncate">{session.type}</span>}
            {session.location_text && (
              <span className="truncate">{session.location_text}</span>
            )}
          </div>
        </div>

        <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-body-faint" fill="none" aria-hidden>
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
    </motion.li>
  );
}
